import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";
import { DateValue } from "@heroui/react";
import { DatePicker } from "@heroui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip } from "@heroui/tooltip";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useDateFormatter } from "@react-aria/i18n";
import { getLocalTimeZone, parseDate } from "@internationalized/date";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/modal";

import useAuth from "../../hooks/useAuth";
import { Form } from "../../components/ui/form";

import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  birthConditions,
  breedingTypes,
  coatColor,
  frameSizes,
  nigerianBreeds,
  paternalTypes,
  ratios,
  statuses,
  vaccineArray,
} from "@/utils/items";
import {
  TUpdateAnimalSchema,
  updateAnimalSchema,
} from "@/models/Animal.Schema.ts";

import { AnimalImageInput } from "@/components/animal-image-input";
import { apiClient } from "@/api/api.ts";

const FarmerAnimalUpdate = ({ data }: any) => {
  const [activeTab, setActiveTab] = useState("basic");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [value, setValue] = useState<DateValue>(parseDate("2024-04-04"));

  const queryClient = useQueryClient();
  const auth = useAuth();
  const id = auth?.UserInfo.id;

  const animalDetails = { ...data.data };

  delete animalDetails.phases;

  const formatter = useDateFormatter({ dateStyle: "full" });

  const form = useForm<TUpdateAnimalSchema>({
    resolver: zodResolver(updateAnimalSchema),
    mode: "onChange",
    defaultValues: {
      // gn: data?.gn.slice ( 2 ) || "",
      gn: data.data?.gn.slice(2) || "",
      sex: data.data?.sex || "",
      name: data.data?.name || "",
      weight: data.data?.weight ? Number(data.data.weight) : undefined,
      vaccination: data.data?.vaccination || "",
      damDGR: data.data?.damDGR || "",
      damBreed: data.data?.damBreed || "",
      damName: data.data?.damName || "",
      sireDGR: data.data?.sireDGR || "",
      sireName: data.data?.sireName || "",
      sireBreed: data.data?.sireBreed || "",
      age: data.data?.age || "",
      paternalGrandDamDGR: data.data?.paternalGrandDamDGR || "",
      paternalGrandDamName: data.data?.paternalGrandDamName || "",
      paternalGrandDamBreed: data.data?.paternalGrandDamBreed || "",
      paternalGrandSireDGR: data.data?.paternalGrandSireDGR || "",
      paternalGrandSireName: data.data?.paternalGrandSireName || "",
      paternalGrandSireBreed: data.data?.paternalGrandSireBreed || "",
      maternalGrandDamDGR: data.data?.maternalGrandDamDGR || "",
      maternalGrandDamName: data.data?.maternalGrandDamName || "",
      maternalGrandDamBreed: data.data?.maternalGrandDamBreed || "",
      maternalGrandSireDGR: data.data?.maternalGrandSireDGR || "",
      maternalGrandSireName: data.data.maternalGrandSireName || "",
      maternalGrandSireBreed: data.data?.maternalGrandSireBreed || "",
      animalStatus: data.data?.animalStatus || "",
      birthWeight: data.data?.birthWeight ? Number(data.data.birthWeight) : undefined,
      coatColor: data.data?.coatColor || "",
      size: data.data?.size || "",
      birthCondition: data.data?.birthCondition || undefined,
      breedingType: data.data?.breedingType || "",
      paternalType: data.data?.paternalType || "",
      racialComposition: data.data?.racialComposition || undefined,
      breedCompositionRatio: data.data?.breedCompositionRatio || "",
    },
  });

  const { mutateAsync } = useMutation({
    mutationKey: ["updateAnimal"],
    mutationFn: async (data) => {
      {
        try {
          const response = await apiClient.post("/animalUpdateRequest", data);

          return response.data;
        } catch (error) {
          console.error(`Error updating animal: ${error}`);
          throw error;
        }
      }
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ["allAnimals"] });
      const previousAnimals = queryClient.getQueryData<any>(["allAnimals"]);

      queryClient.setQueryData<any>(["allAnimals"], (old: any) => {
        if (!old) return [];

        return old.map((animal: any) =>
          animal._id === data.id
            ? {
                ...animal,
                gn: data.data?.gn || "",
                sex: data.data?.sex || "",
                name: data.data?.name || "",
                weight: data.data?.weight || "",
                vaccination: data.data?.vaccination || "",
                damDGR: data.data?.damDGR || "",
                damBreed: data.data?.damBreed || "",
                damName: data.data?.damName || "",
                sireDGR: data.data?.sireDGR || "",
                sireName: data.data?.sireName || "",
                sireBreed: data.data?.sireBreed || "",
                age: data.data?.age || "",
                paternalGrandDamDGR: data.data?.paternalGrandDamDGR || "",
                paternalGrandDamName: data.data?.paternalGrandDamName || "",
                paternalGrandDamBreed: data.data?.paternalGrandDamBreed || "",
                paternalGrandSireDGR: data.data?.paternalGrandSireDGR || "",
                paternalGrandSireName: data.data?.paternalGrandSireName || "",
                paternalGrandSireBreed: data.data?.paternalGrandSireBreed || "",
                maternalGrandDamDGR: data.data?.maternalGrandDamDGR || "",
                maternalGrandDamName: data.data?.maternalGrandDamName || "",
                maternalGrandDamBreed: data.data?.maternalGrandDamBreed || "",
                maternalGrandSireDGR: data.data?.maternalGrandSireDGR || "",
                maternalGrandSireName: data.data.maternalGrandSireName || "",
                maternalGrandSireBreed: data.data?.maternalGrandSireBreed || "",
                animalStatus: data.data?.animalStatus || "",
                birthWeight: data.data?.birthWeight || "",
                coatColor: data.data?.coatColor || "",
                size: data.data?.size || "",
                birthCondition: data.data?.birthCondition || "",
                breedingType: data.data?.breedingType || "",
                paternalType: data.data?.paternalType || "",
                racialComposition: `${data.data?.racialComposition || ""}${
                  data.data?.breedCompositionRatio || ""
                }`,
              }
            : animal,
        );
      });

      return { previousAnimals };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allAnimals"] });
      queryClient.invalidateQueries({ queryKey: ["animals"] });
      toast("The animal information has been updated successfully.");
      // onClose ()
    },
    onError: (error: Error, context: any) => {
      if (error instanceof AxiosError) {
        toast(error.response?.data.message);
      }
      toast("Failed to update animal details. Reverting...");
      queryClient.setQueryData(["allAnimals"], context?.previousAnimals);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["allAnimals"] });
      queryClient.invalidateQueries({ queryKey: ["animals"] });
    },
  });

  const getChangedFields = (currentValues: any, initialData: any) => {
    const changes: any = {};

    Object.keys(currentValues).forEach((key) => {
      if (
        currentValues[key] !== initialData[key] &&
        currentValues[key] !== null &&
        currentValues[key] !== undefined
      ) {
        changes[key] = currentValues[key];
      }
    });

    return changes;
  };

  const onSubmit: SubmitHandler<TUpdateAnimalSchema> = async () => {
    try {
      const animalData = {
        id: data.id,
        gn: "SG" + form.getValues().gn,
        birthDate: formatter.format(value.toDate(getLocalTimeZone())),
        sex: form.getValues().sex,
        name: form.getValues().name,
        age: form.getValues().age,
        size: form.getValues().size,
        weight: Number(form.getValues().weight),
        birthWeight: Number(form.getValues().birthWeight),
        coatColor: form.getValues().coatColor,
        breedingType: form.getValues().breedingType,
        damName: form.getValues().damName,
        damDGR: form.getValues().damDGR,
        damBreed: form.getValues().damBreed,
        sireDGR: form.getValues().sireDGR,
        sireName: form.getValues().sireName,
        sireBreed: form.getValues().sireBreed,
        paternalGrandDamDGR: form.getValues().paternalGrandDamDGR,
        paternalGrandDamName: form.getValues().paternalGrandDamName,
        paternalGrandDamBreed: form.getValues().paternalGrandDamBreed,
        paternalGrandSireDGR: form.getValues().paternalGrandSireDGR,
        paternalGrandSireName: form.getValues().paternalGrandSireName,
        paternalGrandSireBreed: form.getValues().paternalGrandSireBreed,
        maternalGrandDamDGR: form.getValues().maternalGrandDamDGR,
        maternalGrandDamName: form.getValues().maternalGrandDamName,
        maternalGrandDamBreed: form.getValues().maternalGrandDamBreed,
        maternalGrandSireDGR: form.getValues().maternalGrandSireDGR,
        maternalGrandSireName: form.getValues().maternalGrandSireName,
        maternalGrandSireBreed: form.getValues().maternalGrandSireBreed,
        birthCondition: form.getValues().birthCondition,
        paternalType: form.getValues().paternalType,
        racialComposition: `${form.getValues().racialComposition || ""}${form.getValues().breedCompositionRatio || ""}`,
        vaccination: form.getValues().vaccination,
        animalStatus: form.getValues().animalStatus,
        farmerID: id || "",
        farmID: data.farm,
      };

      const changedFields = getChangedFields(animalData, data.data);
      if (imageFile) {
        const { uploadAnimalImage } = await import("@/utils/uploadAnimalImage");
        changedFields.image = await uploadAnimalImage(imageFile);
      }

      const clientData = {
        gn: data.data.gn,
        animalId: data.data._id,
        farmerId: data.data.farmer,
        assignedSupervisorId: data.data.assignedSupervisor,
        requestedChanges: changedFields,
        changeReason: "updating",
      };

      const update = mutateAsync(clientData);
      toast.promise(update, {
        loading: "Updating animal...",
        success: "Animal details updated successfully!",
        error: "Failed to update animal details.",
      });
      await update;
      setImageFile(null);
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : "Failed to update animal details.");
    }
  };

  const tabs = [
    { id: "basic", label: "Basic Info" },
    { id: "family", label: "Family Info" },
    { id: "additional", label: "Additional Info" },
  ];

  return (
    <div className="container mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="col-span-1 dark:bg-gray-900">
          <CardContent className={"py-2"}>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="bg-primary-100 text-gray-800 dark:bg-primary-300 grid w-full grid-cols-3">
                    {tabs.map((tab) => (
                      <TabsTrigger
                        key={tab.id}
                        className="data-[state=active]:dark:bg-amber-600 data-[state=active]:bg-amber-600 data-[state=active]:text-primary-foreground"
                        value={tab.id}
                      >
                        {tab.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeTab}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      initial={{ opacity: 0, y: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <TabsContent className="mt-4" value="basic">
                        <ScrollArea className="h-[60vh] pr-4">
                          <BasicInfoFields
                            form={form}
                            receivedData={animalDetails}
                            setValue={setValue}
                            value={value}
                          />
                        </ScrollArea>
                      </TabsContent>
                      <TabsContent className="mt-4" value="family">
                        <ScrollArea className="h-[60vh] pr-4">
                          <FamilyInfoFields
                            form={form}
                            receivedData={animalDetails}
                          />
                        </ScrollArea>
                      </TabsContent>
                      <TabsContent className="mt-4 " value="additional">
                        <ScrollArea className="h-[60vh] pr-4">
                          <AdditionalInfoFields
                            form={form}
                            receivedData={animalDetails}
                            imageFile={imageFile}
                            onImageChange={setImageFile}
                          />
                        </ScrollArea>
                      </TabsContent>
                    </motion.div>
                  </AnimatePresence>
                </Tabs>
                <Button
                  className="w-full mt-6"
                  color={"primary"}
                  radius={"sm"}
                  type="submit"
                  isDisabled={form.formState.isSubmitting}
                  isLoading={form.formState.isSubmitting}
                >
                  Submit Changes
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Received Animal Data</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[70vh] pr-4">
              <AnimatePresence>
                {Object.entries(animalDetails || {}).map(
                  ([key, value], index) => (
                    <motion.div
                      key={`${key}-${index}`}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4"
                      initial={{ opacity: 0, y: 20 }}
                      transition={{
                        duration: 0.3,
                        delay: index * 0.05,
                      }}
                    >
                      <Label className="text-sm font-medium text-muted-foreground">
                        {key}
                      </Label>
                      <div className="mt-1 p-2 bg-muted rounded-md">
                        <span className="text-base font-semibold">
                          {/*{ typeof value === 'object' ? JSON.stringify ( value, null, 2 ) : value }*/}
                          {value as string}
                        </span>
                      </div>
                    </motion.div>
                  ),
                )}
              </AnimatePresence>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const BasicInfoFields = ({
  form,
  receivedData,
  value,
  setValue,
}: {
  form: any;
  receivedData: any;
  setValue: (value: DateValue) => void;
  value: any;
}) => {
  const { control } = form;

  return (
    <div className="space-y-4">
      <Controller
        control={control}
        name="gn"
        render={({ field }) => (
          <InputField
            field={field}
            label="Birth Registry ID"
            name="gn"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="name"
        render={({ field }) => (
          <InputField
            field={field}
            label="Name"
            name="name"
            receivedData={receivedData}
          />
        )}
      />
      <div className="space-y-2 ">
        <Tooltip
          color="secondary"
          content="The Birth Date of The Animal"
          delay={500}
          offset={10}
        >
          <DatePicker
            showMonthAndYearPickers
            calendarProps={{
              focusedValue: value,
              onFocusChange: setValue,
              nextButtonProps: {
                variant: "bordered",
              },
              prevButtonProps: {
                variant: "bordered",
              },
            }}
            className={"w-[300px]"}
            description={"date format mm/dd/yyyy"}
            label="Birth date"
            labelPlacement={"outside"}
            size="sm"
            value={value}
            onChange={(val) => val && setValue(val)}
          />
        </Tooltip>
      </div>
      <Controller
        control={control}
        name="sex"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Sex"
            name="sex"
            options={[
              { value: "male", label: "Male" },
              { value: "female", label: "Female" },
            ]}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="age"
        render={({ field }) => (
          <InputField
            field={field}
            label="Age"
            name="age"
            receivedData={receivedData}
            type="number"
          />
        )}
      />
      <Controller
        control={control}
        name="weight"
        render={({ field }) => (
          <InputField
            field={field}
            label="Weight"
            name="weight"
            receivedData={receivedData}
            type="number"
          />
        )}
      />
      <Controller
        control={control}
        name="birthWeight"
        render={({ field }) => (
          <InputField
            field={field}
            label="Birth Weight"
            name="birthWeight"
            receivedData={receivedData}
            type="string"
          />
        )}
      />
      <Controller
        control={control}
        name="animalStatus"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Status"
            name="animalStatus"
            options={statuses.map((s) => ({
              value: s.uid,
              label: s.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="vaccination"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Vaccines"
            name="vaccination"
            options={vaccineArray.map((v) => ({
              value: v.value,
              label: v.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="breedingType"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Breeding Type"
            name="breedingType"
            options={breedingTypes.map((b) => ({
              value: b.name,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="size"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Size"
            name="size"
            options={frameSizes.map((s) => ({
              value: s.value,
              label: s.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="coatColor"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Coat Color"
            name="coatColor"
            options={coatColor.map((c) => ({
              value: c.coatColor,
              label: c.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="racialComposition"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Racial Composition"
            name="racialComposition"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="breedCompositionRatio"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Breed Composition Ratio"
            name="breedCompositionRatio"
            options={ratios.map((r) => ({
              value: r.value,
              label: r.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
    </div>
  );
};

const FamilyInfoFields = ({ form, receivedData }: any) => {
  const { control } = form;

  return (
    <div className="space-y-4">
      <Controller
        control={control}
        name="damName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Dam Name"
            name="damName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="damDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Dam DGR"
            name="damDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="damBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Dam Breed"
            name="damBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="sireName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Sire Name"
            name="sireName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="sireDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Sire DGR"
            name="sireDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="sireBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Sire Breed"
            name="sireBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandDamName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Paternal GrandDam Name"
            name="paternalGrandDamName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandDamDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Paternal GrandDam DGR"
            name="paternalGrandDamDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandDamBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Paternal GrandDam Breed"
            name="paternalGrandDamBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandSireName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Paternal GrandSire Name"
            name="paternalGrandSireName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandSireDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Paternal GrandSire DGR"
            name="paternalGrandSireDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="paternalGrandSireBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Paternal GrandSire Breed"
            name="paternalGrandSireBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandDamName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Maternal GrandDam Name"
            name="maternalGrandDamName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandDamDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Maternal GrandDam DGR"
            name="maternalGrandDamDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandDamBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Maternal GrandDam Breed"
            name="maternalGrandDamBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandSireName"
        render={({ field }) => (
          <InputField
            field={field}
            label="Maternal GrandSire Name"
            name="maternalGrandSireName"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandSireDGR"
        render={({ field }) => (
          <InputField
            field={field}
            label="Maternal GrandSire DGR"
            name="maternalGrandSireDGR"
            receivedData={receivedData}
          />
        )}
      />
      <Controller
        control={control}
        name="maternalGrandSireBreed"
        render={({ field }) => (
          <SelectField
            field={field}
            label="Maternal GrandSire Breed"
            name="maternalGrandSireBreed"
            options={nigerianBreeds.map((b) => ({
              value: b.breed,
              label: b.label,
            }))}
            receivedData={receivedData}
          />
        )}
      />
    </div>
  );
};

const AdditionalInfoFields = ({ form, receivedData, imageFile, onImageChange }: any) => {
  const { control } = form;

  return (
    <div className="w-full space-y-4 flex flex-col">
      <div className={"w-full flex flex-col gap-4"}>
        <Controller
          control={control}
          name="paternalType"
          render={({ field }) => (
            <SelectField
              field={field}
              label="Paternal Type"
              name="paternalType"
              options={paternalTypes.map((t) => ({
                value: t.value,
                label: t.label,
              }))}
              receivedData={receivedData}
            />
          )}
        />
        <Controller
          control={control}
          name="birthCondition"
          render={({ field }) => (
            <SelectField
              field={field}
              label="Birth Condition"
              name="birthCondition"
              options={birthConditions.map((c) => ({
                value: c.uid,
                label: c.label,
              }))}
              receivedData={receivedData}
            />
          )}
        />
      </div>
      <AnimalImageInput file={imageFile} currentImage={receivedData?.image} onChange={onImageChange} disabled={form.formState.isSubmitting} />
    </div>
  );
};

const InputField = ({
  name,
  label,
  type,
  field,
  receivedData = {}, // Default to an empty object
}: any) => {
  // console.log ( `InputField - ${ name }:`, {
  // 	fieldValue: field.value,
  // 	receivedData: receivedData[ name ],
  // } )
  return (
    <div className="space-y-2">
      <Label htmlFor={name}>{label}</Label>
      <Input
        className="w-full "
        classNames={{
          inputWrapper: "dark:bg-slate-950",
        }}
        color={"primary"}
        id={name}
        name={name}
        size={"sm"}
        type={type}
        value={field.value || ""}
        variant={"faded"}
        onChange={field.onChange}
      />
      {receivedData[name] !== undefined && ( // Check if receivedData[name] is defined
        <p className="text-sm text-muted-foreground flex items-center">
          <span className="mr-1">Current:</span>
          <span className="font-medium">{receivedData[name]}</span>
          {field.value && field.value !== receivedData[name] && (
            <motion.span
              animate={{ opacity: 1, x: 0 }}
              className="ml-2 text-primary dark:text-rose-400"
              initial={{ opacity: 0, x: -10 }}
            >
              <CheckCircle2 className="w-4 h-4 inline-block mr-1" />
              Changed
            </motion.span>
          )}
        </p>
      )}
    </div>
  );
};

const SelectField = ({
  name,
  label,
  options,
  field,
  receivedData = {},
}: any) => {
  const [triggerElement, setTriggerElement] = useState<HTMLButtonElement | null>(null);

  return (
    <div className="space-y-2">
      <Label htmlFor={name}>{label}</Label>
      <Select
        defaultValue={field.value || undefined}
        value={field.value || undefined}
        onValueChange={field.onChange}
      >
        <SelectTrigger ref={setTriggerElement} id={name} className="w-full">
          <SelectValue placeholder={`Select ${label}`} />
        </SelectTrigger>
        <SelectContent
          position="popper"
          // Keep options inside HeroUI's modal focus scope.
          container={triggerElement?.closest<HTMLElement>('[role="dialog"]')}
          className="max-h-[300px] overflow-y-auto z-[9999] shadow-lg bg-white text-slate-950 dark:bg-slate-950 dark:text-slate-50"
        >
          {options.map((option: any) => (
            <SelectItem
              key={String(option.value)}
              value={String(option.value)}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {receivedData[name] !== undefined && ( // Check if receivedData[name] is defined
        <p className="text-sm text-muted-foreground flex items-center">
          <span className="mr-1">Current:</span>
          <span className="font-medium">{receivedData[name]}</span>
          {field.value && field.value !== receivedData[name] && (
            <motion.span
              animate={{ opacity: 1, x: 0 }}
              className="ml-2 text-primary dark:text-rose-400"
              initial={{ opacity: 0, x: -10 }}
            >
              <CheckCircle2 className="w-4 h-4 inline-block mr-1" />
              Changed
            </motion.span>
          )}
        </p>
      )}
    </div>
  );
};

export default function FarmerUpdateAnimalWrapper(animalDetails: any) {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  return (
    <>
      <Button color={"secondary"} size={"sm"} onPress={onOpen}>
        update
      </Button>
      <Modal
        isOpen={isOpen}
        scrollBehavior={"inside"}
        size={"5xl"}
        onOpenChange={onOpenChange}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex bg-slate-600 flex-col gap-1">
                Animal Details Update Form
              </ModalHeader>
              <ModalBody>
                <FarmerAnimalUpdate data={animalDetails} />
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light">
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
